import appConfig from '@/config/appConfig.typed';

import {areSsoCookiesMissing} from './ssoCookieService';

export type SsoLinkSessionInfoFlags = {
    openInBluefy?: boolean;
    isSsoCookiesForced: boolean;
};

/**
 * Whether cookie SSO applies for this link.
 * `isSsoCookiesForced` is one-way: when false, only the global flag applies; when true, opts in if global is off.
 */
export const isSsoCookieFlowEnabledForLink = ({openInBluefy, isSsoCookiesForced}: SsoLinkSessionInfoFlags): boolean => {
    if (openInBluefy) return false;

    return Boolean(appConfig.getIsSsoCookiesEnabled()) || isSsoCookiesForced;
};

/** Batch: IncludeSessionInfo when at least one queued link allows cookie SSO. */
export const batchAllowsSessionInfo = (links: SsoLinkSessionInfoFlags[]): boolean =>
    links.some(isSsoCookieFlowEnabledForLink);

export type IccLoginTokenListSessionInfoProps = {
    isDcxMarket: boolean;
    /**
     * false when SessionInfo must not be requested (e.g. `openInBluefy`, or a debounced batch
     * where every queued item is Bluefy-only, or global off with no per-link forced link — see `batchAllowSsoCookies`
     * in useSsoLinkBatch).
     */
    allowSessionInfo: boolean;
    /**
     * Timestamp of the last successful cookie application (`recordSsoSessionApplied()`).
     * `null` means we don't know the cookie age yet (e.g. after a page reload).
     */
    lastSsoSessionApplyAt: number | null;
};

const areCookiesMissingOrUnknown = (lastSsoSessionApplyAt: number | null): boolean =>
    areSsoCookiesMissing() || lastSsoSessionApplyAt == null;

/**
 * Whether `CreateLoginTokenList` / `getSsoLinks` should send `IncludeSessionInfo: true`.
 * `allowSessionInfo` already reflects per-link cookie eligibility (global on or per-link forced).
 */
export const shouldRequestIccLoginTokenListSessionInfo = (props: IccLoginTokenListSessionInfoProps): boolean =>
    props.isDcxMarket && props.allowSessionInfo && areCookiesMissingOrUnknown(props.lastSsoSessionApplyAt);

/**
 * Whether SSO cookie refresh / cookie-oriented batch behaviour applies (config + DCX + not Bluefy).
 * Does not depend on current cookie presence — used for scheduling and hook `disabled` flags.
 */
export const isSsoCookiesFlowActive = (props: {isDcxMarket: boolean; openInBluefy?: boolean}): boolean =>
    Boolean(appConfig.getIsSsoCookiesEnabled()) && props.isDcxMarket && !props.openInBluefy;
